import { FORMAT_BYTES, type ExternalImageSource, type TexFormat } from './types.js';

/** Usage mask every pooled texture gets, minus the render-attachment bit. */
const BASE_TEXTURE_USAGE =
  GPUTextureUsage.TEXTURE_BINDING |
  GPUTextureUsage.STORAGE_BINDING |
  GPUTextureUsage.COPY_SRC |
  GPUTextureUsage.COPY_DST;

/**
 * `copyExternalImageToTexture` requires RENDER_ATTACHMENT on the destination,
 * and only 8-bit / 16-bit-float colour formats are valid destinations, so we
 * only pay for that bit where it can actually be used.
 */
const RENDERABLE_FORMATS: ReadonlySet<TexFormat> = new Set<TexFormat>([
  'rgba8unorm',
  'rgba16float',
]);

export function defaultTextureUsage(format: TexFormat): number {
  return RENDERABLE_FORMATS.has(format)
    ? BASE_TEXTURE_USAGE | GPUTextureUsage.RENDER_ATTACHMENT
    : BASE_TEXTURE_USAGE;
}

/**
 * A 2D GPU texture plus the metadata the kernels need.
 *
 * One `GPUTextureView` is cached and reused for both sampled (`texture_2d<f32>`,
 * always read with `textureLoad`) and storage (`texture_storage_2d<F, write>`)
 * bindings - a default view of a single-mip 2D texture is valid for both.
 */
export class GpuImage {
  #view: GPUTextureView | null = null;
  #destroyed = false;

  constructor(
    readonly texture: GPUTexture,
    readonly width: number,
    readonly height: number,
    readonly format: TexFormat,
    /** Set when the image came from a pool and should be recycled, not destroyed. */
    private readonly recycle: ((image: GpuImage) => void) | null = null,
  ) {}

  static create(
    device: GPUDevice,
    width: number,
    height: number,
    format: TexFormat,
    label?: string,
  ): GpuImage {
    if (width <= 0 || height <= 0) {
      throw new Error(`[gpu-core] invalid texture size ${width}x${height}`);
    }
    const texture = device.createTexture({
      label: label ?? `${format} ${width}x${height}`,
      size: { width, height },
      format,
      usage: defaultTextureUsage(format),
    });
    return new GpuImage(texture, width, height, format);
  }

  get view(): GPUTextureView {
    if (this.#destroyed) throw new Error('[gpu-core] use of a destroyed GpuImage');
    this.#view ??= this.texture.createView({ label: this.texture.label });
    return this.#view;
  }

  get byteSize(): number {
    return this.width * this.height * FORMAT_BYTES[this.format];
  }

  get sizeVec(): [number, number] {
    return [this.width, this.height];
  }

  matches(width: number, height: number, format: TexFormat): boolean {
    return this.width === width && this.height === height && this.format === format;
  }

  /** Returns the texture to its pool, or destroys it when unpooled. */
  release(): void {
    if (this.#destroyed) return;
    if (this.recycle) this.recycle(this);
    else this.destroy();
  }

  destroy(): void {
    if (this.#destroyed) return;
    this.#destroyed = true;
    this.#view = null;
    this.texture.destroy();
  }

  get destroyed(): boolean {
    return this.#destroyed;
  }
}

/**
 * Recycles textures by (size, format).
 *
 * The pipeline allocates and frees a lot of full-resolution intermediates -
 * guided-filter moments alone are three `rgba32float` surfaces - and creating a
 * 4K texture is expensive enough to show up in frame times when the user drags a
 * slider. Aliasing between passes is safe because WebGPU guarantees memory
 * visibility between consecutive dispatches in a pass.
 */
export class TexturePool {
  readonly #free = new Map<string, GpuImage[]>();
  #liveBytes = 0;
  #pooledBytes = 0;

  constructor(
    private readonly device: GPUDevice,
    /** Textures beyond this budget are destroyed instead of pooled. */
    private readonly budgetBytes = 512 * 1024 * 1024,
  ) {}

  acquire(width: number, height: number, format: TexFormat, label?: string): GpuImage {
    const key = keyOf(width, height, format);
    const bucket = this.#free.get(key);
    const reused = bucket?.pop();
    if (reused) {
      this.#pooledBytes -= reused.byteSize;
      this.#liveBytes += reused.byteSize;
      return reused;
    }
    const fresh = GpuImage.create(this.device, width, height, format, label);
    this.#liveBytes += fresh.byteSize;
    // Re-wrap so `release()` sends it back here rather than destroying it.
    return new GpuImage(fresh.texture, width, height, format, (img) => this.#put(key, img));
  }

  #put(key: string, image: GpuImage): void {
    this.#liveBytes -= image.byteSize;
    if (this.#pooledBytes + image.byteSize > this.budgetBytes) {
      image.destroy();
      return;
    }
    this.#pooledBytes += image.byteSize;
    const bucket = this.#free.get(key);
    if (bucket) bucket.push(image);
    else this.#free.set(key, [image]);
  }

  get stats(): { liveBytes: number; pooledBytes: number; buckets: number } {
    return { liveBytes: this.#liveBytes, pooledBytes: this.#pooledBytes, buckets: this.#free.size };
  }

  /** Drops every cached texture; live textures are untouched. */
  trim(): void {
    for (const bucket of this.#free.values()) for (const image of bucket) image.destroy();
    this.#free.clear();
    this.#pooledBytes = 0;
  }
}

function keyOf(width: number, height: number, format: TexFormat): string {
  return `${width}x${height}:${format}`;
}

/** Recycles storage buffers by (rounded size, usage). */
export class BufferPool {
  readonly #free = new Map<string, GPUBuffer[]>();

  constructor(private readonly device: GPUDevice) {}

  acquire(byteSize: number, usage: number, label?: string): GPUBuffer {
    // Round up to the next power of two so slightly different tensor shapes
    // still hit the cache.
    const size = Math.max(256, nextPow2(byteSize));
    const key = `${size}:${usage}`;
    const bucket = this.#free.get(key);
    const reused = bucket?.pop();
    if (reused) return reused;
    return this.device.createBuffer({ label: label ?? key, size, usage });
  }

  release(buffer: GPUBuffer): void {
    const key = `${buffer.size}:${buffer.usage}`;
    const bucket = this.#free.get(key);
    if (bucket) bucket.push(buffer);
    else this.#free.set(key, [buffer]);
  }

  trim(): void {
    for (const bucket of this.#free.values()) for (const buffer of bucket) buffer.destroy();
    this.#free.clear();
  }
}

function nextPow2(value: number): number {
  let v = Math.max(1, Math.ceil(value));
  v--;
  v |= v >> 1;
  v |= v >> 2;
  v |= v >> 4;
  v |= v >> 8;
  v |= v >> 16;
  return v + 1;
}

/**
 * Bump allocator for per-dispatch uniform blocks.
 *
 * Every kernel wants a handful of scalars; creating one small `GPUBuffer` per
 * dispatch is a measurable cost when a matte refresh issues ~40 dispatches. We
 * sub-allocate from 1 MiB chunks at the required 256-byte binding alignment and
 * reset the whole ring after each submit.
 */
export class UniformRing {
  static readonly CHUNK = 1 << 20;
  readonly #chunks: GPUBuffer[] = [];
  #chunkIndex = 0;
  #offset = 0;
  readonly #alignment: number;

  constructor(private readonly device: GPUDevice) {
    this.#alignment = Math.max(256, device.limits.minUniformBufferOffsetAlignment);
  }

  /** Uploads `data` and returns a binding pointing at it. */
  write(data: ArrayBufferView): GPUBufferBinding {
    const size = align(data.byteLength, this.#alignment);
    if (size > UniformRing.CHUNK) {
      throw new Error(`[gpu-core] uniform block of ${data.byteLength} B exceeds the ring chunk size`);
    }
    if (this.#offset + size > UniformRing.CHUNK) {
      this.#chunkIndex++;
      this.#offset = 0;
    }
    while (this.#chunks.length <= this.#chunkIndex) {
      this.#chunks.push(
        this.device.createBuffer({
          label: `uniform-ring#${this.#chunks.length}`,
          size: UniformRing.CHUNK,
          usage: GPUBufferUsage.UNIFORM | GPUBufferUsage.COPY_DST,
        }),
      );
    }
    const buffer = this.#chunks[this.#chunkIndex]!;
    const offset = this.#offset;
    this.#offset += size;
    this.device.queue.writeBuffer(buffer, offset, data.buffer, data.byteOffset, data.byteLength);
    return { buffer, offset, size };
  }

  /**
   * Rewinds the ring. Only safe once the commands referencing it are submitted -
   * `queue.writeBuffer` copies eagerly, so submitted work keeps its own snapshot.
   */
  reset(): void {
    this.#chunkIndex = 0;
    this.#offset = 0;
  }

  destroy(): void {
    for (const chunk of this.#chunks) chunk.destroy();
    this.#chunks.length = 0;
  }
}

function align(value: number, alignment: number): number {
  return Math.ceil(value / alignment) * alignment;
}

/** Uploads an `ImageBitmap`-like source into a fresh texture. */
export function textureFromExternalImage(
  device: GPUDevice,
  source: ExternalImageSource,
  width: number,
  height: number,
  format: TexFormat = 'rgba8unorm',
  flipY = false,
): GpuImage {
  if (!RENDERABLE_FORMATS.has(format)) {
    throw new Error(`[gpu-core] ${format} cannot receive an external image copy`);
  }
  const image = GpuImage.create(device, width, height, format, 'source-image');
  device.queue.copyExternalImageToTexture(
    { source: source as GPUCopyExternalImageSource, flipY },
    { texture: image.texture, colorSpace: 'srgb', premultipliedAlpha: false },
    { width, height },
  );
  return image;
}
